import React from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { BLUE_RAMP, CHART_INK, formatUsdCompact, formatUsdFull } from './common';

interface RankedBarChartProps {
  /** Already ranked, highest first. */
  data: Array<{ name: string; value: number }>;
  valueFormatter?: (value: number) => string;
  /** Tooltip series label (defaults to Deposits for gateway ranking). */
  valueLabel?: string;
}

/**
 * Horizontal ranked bars for a league table (deposits by gateway / top entities).
 *
 * Identity is the row label, so the blue ramp is used as a rank cue — darkest
 * at the top — rather than as a category encoding. Values are printed at the
 * end of each bar so the chart is readable without reaching for the axis.
 */
const RankedBarChart: React.FC<RankedBarChartProps> = ({
  data,
  valueFormatter = formatUsdFull,
  valueLabel = 'Deposits',
}) => (
  <ResponsiveContainer width="100%" height="100%">
    <BarChart
      data={data}
      layout="vertical"
      margin={{ top: 4, right: 64, bottom: 4, left: 4 }}
      barCategoryGap="28%"
    >
      <CartesianGrid stroke={CHART_INK.grid} horizontal={false} />
      <XAxis
        type="number"
        tickFormatter={formatUsdCompact}
        tick={{ fontSize: 11, fill: CHART_INK.muted }}
        tickLine={false}
        axisLine={false}
      />
      <YAxis
        type="category"
        dataKey="name"
        width={104}
        tick={{ fontSize: 12, fill: CHART_INK.primary }}
        tickLine={false}
        axisLine={false}
      />
      <Tooltip
        formatter={(value: number) => [valueFormatter(value), valueLabel]}
        cursor={{ fill: 'rgba(37, 99, 235, 0.06)' }}
        contentStyle={{ borderRadius: 10, border: `1px solid ${CHART_INK.grid}`, fontSize: 12 }}
      />
      <Bar dataKey="value" radius={[0, 6, 6, 0]} maxBarSize={22} isAnimationActive={false}>
        {data.map((entry, index) => (
          <Cell key={entry.name} fill={BLUE_RAMP[Math.min(index, BLUE_RAMP.length - 1)]} />
        ))}
        <LabelList
          dataKey="value"
          position="right"
          formatter={(value: number) => formatUsdCompact(value)}
          style={{ fontSize: 11, fill: CHART_INK.primary, fontWeight: 500 }}
        />
      </Bar>
    </BarChart>
  </ResponsiveContainer>
);

export default RankedBarChart;
